// src/components/documents/DocumentChat.tsx
import { useState, useEffect, useRef, useMemo } from "react";
import {
  MessageCircle,
  FileText,
  Download,
  Check,
  CheckCheck,
  Send,
  Paperclip,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/store/authStore";
import { useNotificationStore } from "@/store/notificationStore";
import { useDocumentStore } from "@/store/documentStore";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import type { DocumentMessage as Message } from "@/types/document";
import { toast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// === ДИЗАЙН-СЛОЙ ===
import { documentChatTheme, MessageBubble } from "./ui/DocumentChatUI";

interface DocumentChatProps {
  docId: string;
}

const getTimestamp = (ts: any): string => {
  if (!ts) return "";
  const date = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const getInitials = (name: string) =>
  name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "??";

const formatFullDate = (ts: any) => {
  if (!ts) return "";
  const date = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export default function DocumentChat({ docId }: DocumentChatProps) {
  const { user } = useAuthStore();
  const { markDocumentAsRead } = useNotificationStore();
  const { init } = useDocumentStore();

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const currentUserId = user ? Number(user.id) : 0;
  const currentUserIdStr = currentUserId.toString();

  const canEditMessage = (msg: Message): boolean => {
    if (msg.isSystem) return false;
    if (Number(msg.senderId) !== currentUserId) return false;
    if (!msg.timestamp) return false;
    const messageTime = msg.timestamp instanceof Timestamp
      ? msg.timestamp.toDate()
      : new Date(msg.timestamp);
    const now = new Date();
    const diffMs = now.getTime() - messageTime.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  useEffect(() => {
    if (!docId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    updateDoc(doc(db, "documents", docId), {
      [`unreadMessagesBy.${currentUserIdStr}`]: 0,
    }).catch(() => {});

    markDocumentAsRead(docId);

    const messagesRef = collection(db, "documents", docId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(q, (snap) => {
      const loaded = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Message));
      setMessages(loaded);
      setLoading(false);

      const container = scrollRef.current;
      if (container) {
        const wasAtBottom =
          Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 120 ||
          prevScrollHeightRef.current === 0;

        if (wasAtBottom) {
          container.scrollTop = container.scrollHeight;
        }
        prevScrollHeightRef.current = container.scrollHeight;
      }
    });

    return () => unsubscribe();
  }, [docId, currentUserIdStr, markDocumentAsRead]);

  useEffect(() => {
    if (!docId || !user) return;

    const typingRef = doc(db, "documents", docId, "meta", "typing");
    const unsubscribe = onSnapshot(typingRef, (snap) => {
      const data = snap.data();
      const typing = Object.entries(data || {}).filter(([k]) => Number(k) !== currentUserId);
      setTypingUser(typing.length > 0 ? (typing[0][1] as string) : null);
    });

    let timeout: NodeJS.Timeout;
    const handleTyping = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {}, 3000);
    };

    document.addEventListener("input", handleTyping, true);
    return () => {
      unsubscribe();
      document.removeEventListener("input", handleTyping, true);
      clearTimeout(timeout);
    };
  }, [docId, user, currentUserId]);

  const handleMessageInteraction = (msg: Message) => {
    if (msg.isSystem) return;
    if (Number(msg.senderId) === currentUserId && canEditMessage(msg)) {
      setEditingMessageId(msg.id);
      setEditText(msg.text || "");
    } else {
      setReplyTo(msg);
    }
  };

  const saveEdit = async (messageId: string) => {
    if (!editText.trim()) return;
    try {
      await updateDoc(doc(db, "documents", docId, "messages", messageId), {
        text: editText.trim(),
        edited: true,
        editedAt: serverTimestamp(),
      });
      setEditingMessageId(null);
      setEditText("");
      toast({ title: "Сообщение изменено" });
    } catch (e) {
      toast({ title: "Ошибка", description: "Не удалось изменить", variant: "destructive" });
    }
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditText("");
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  const sendMessage = async () => {
    if (!user || (!messageText.trim() && !attachedFile) || isSending || !docId) return;
    setIsSending(true);

    try {
      let fileData = null;
      if (attachedFile) {
        const fileRef = ref(storage, `documents/${docId}/messages/${Date.now()}_${attachedFile.name}`);
        await uploadBytes(fileRef, attachedFile);
        fileData = { name: attachedFile.name, url: await getDownloadURL(fileRef) };
      }

      const msg = {
        text: messageText.trim(),
        senderId: user.id,
        senderName: user.name,
        timestamp: serverTimestamp(),
        file: fileData,
        readBy: [currentUserIdStr],
        replyTo: replyTo
          ? {
              messageId: replyTo.id,
              text: replyTo.text.slice(0, 120) + (replyTo.text.length > 120 ? "..." : ""),
              senderName: replyTo.senderName,
            }
          : null,
      };

      await addDoc(collection(db, "documents", docId, "messages"), msg);

      const docRef = doc(db, "documents", docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const ownerIdStr = String(data.ownerId || "");
        const approvers: string[] = (data.approvers || []).map(String);
        const currentIndex = Number(data.currentApproverIndex ?? 0);

        const shouldNotify = new Set<string>();
        shouldNotify.add(ownerIdStr);

        if (currentIndex < approvers.length) {
          const currentApproverId = String(approvers[currentIndex] || "");
          if (currentApproverId) {
            shouldNotify.add(currentApproverId);
          }
        }

        shouldNotify.delete(currentUserIdStr);

        if (shouldNotify.size > 0) {
          const currentUnread = data.unreadMessagesBy || {};
          const updates: Record<string, number> = { ...currentUnread };

          shouldNotify.forEach((id) => {
            updates[id] = (updates[id] || 0) + 1;
          });

          await updateDoc(docRef, { unreadMessagesBy: updates });

          const updatedDoc = {
            ...data,
            id: docId,
            unreadMessagesBy: updates,
            updatedAt: new Date(),
          };

          useDocumentStore.setState((state) => ({
            documents: state.documents.map((d) =>
              d.id === docId ? { ...d, ...updatedDoc } : d
            ),
            myDocuments: state.myDocuments.map((d) =>
              d.id === docId ? { ...d, ...updatedDoc } : d
            ),
            documentsForApproval: state.documentsForApproval.map((d) =>
              d.id === docId ? { ...d, ...updatedDoc } : d
            ),
          }));
        }
      }

      setMessageText("");
      setAttachedFile(null);
      setReplyTo(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      toast({ title: "Сообщение отправлено" });

      setTimeout(() => {
        lastMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 300);
    } catch (e) {
      toast({ title: "Ошибка", description: "Не удалось отправить", variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const filteredMessages = useMemo(() => {
    const safeMessages = messages || [];
    return searchQuery
      ? safeMessages.filter((m) => m.text?.toLowerCase().includes(searchQuery.toLowerCase()))
      : safeMessages;
  }, [messages, searchQuery]);

  const chatFiles = useMemo(() => {
    const safeMessages = messages || [];
    return safeMessages
      .filter((msg) => msg.file && msg.timestamp)
      .sort((a, b) => {
        const timeA = a.timestamp instanceof Timestamp ? a.timestamp.toMillis() : new Date(a.timestamp).getTime();
        const timeB = b.timestamp instanceof Timestamp ? b.timestamp.toMillis() : new Date(b.timestamp).getTime();
        return timeB - timeA;
      })
      .map((msg) => ({
        ...msg.file!,
        timestamp: msg.timestamp,
      }));
  }, [messages]);

  const forceDownload = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) throw new Error(`Не удалось загрузить файл: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Ошибка скачивания:", err);
      window.open(url, "_blank");
      toast({
        title: "Не удалось скачать",
        description: "Файл открыт в новой вкладке",
        variant: "default",
      });
    }
  };

  return (
    <div className={documentChatTheme.mainContainer}>
      <div className={documentChatTheme.searchHeader}>
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по сообщениям..."
            className={documentChatTheme.searchInput}
          />
        </div>
      </div>

      <div className={documentChatTheme.chatArea} ref={scrollRef}>
        {loading ? (
          <div className="flex justify-center items-center h-full text-purple-400 text-base">
            Загрузка сообщений...
          </div>
        ) : messages.length === 0 ? (
          <div className={documentChatTheme.emptyState}>
            <MessageCircle className="h-16 w-16 opacity-40" />
            <div className="text-center">
              <p className="text-slate-300 text-xl font-medium">Сообщений пока нет</p>
              <p className="text-slate-500 mt-3 text-base">Напиши первым!</p>
            </div>
          </div>
        ) : (
          filteredMessages.map((msg, index) => {
            const isOwn = Number(msg.senderId) === currentUserId;
            const prevMsg = index > 0 ? filteredMessages[index - 1] : null;
            const isEditing = editingMessageId === msg.id;
            const isLast = index === filteredMessages.length - 1;
            const currentDay = formatFullDate(msg.timestamp);
            const prevDay = prevMsg ? formatFullDate(prevMsg.timestamp) : null;
            const totalParticipants = 2;
            const readCount = msg.readBy?.length || 1;
            const allRead = readCount >= totalParticipants;
            const isFirstInGroup = !prevMsg || Number(prevMsg.senderId) !== Number(msg.senderId);

            if (msg.isSystem) {
              return (
                <div key={msg.id || index} className="my-4">
                  {prevDay && currentDay !== prevDay && (
                    <div className="w-full my-8 text-center">
                      <span className={documentChatTheme.dateSeparator}>
                        {currentDay}
                      </span>
                    </div>
                  )}
                  <div className={documentChatTheme.systemMessage}>
                    <div className={documentChatTheme.systemBubble}>
                      {msg.text}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div ref={isLast ? lastMessageRef : null} key={msg.id || index}>
                {prevDay && currentDay !== prevDay && (
                  <div className="w-full my-8 text-center">
                    <span className={documentChatTheme.dateSeparator}>
                      {currentDay}
                    </span>
                  </div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={cn(
                    "flex items-end mb-1.5 relative group",
                    isOwn ? "justify-end" : "justify-start"
                  )}
                >
                  {!isOwn && (
                    <Avatar className="mb-1 mr-3 h-8 w-8 flex-shrink-0">
                      <AvatarFallback className={documentChatTheme.avatarOther}>
                        {getInitials(msg.senderName)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {!isOwn || <div className="w-11 flex-shrink-0" />}

                  <MessageBubble isOwn={isOwn} isFirstInGroup={isFirstInGroup}
                    onDoubleClick={() => handleMessageInteraction(msg)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      longPressTimerRef.current = setTimeout(() => handleMessageInteraction(msg), 400);
                    }}
                    onTouchEnd={() => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                        longPressTimerRef.current = null;
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                        longPressTimerRef.current = null;
                      }
                    }}
                  >
                    {msg.replyTo && (
                      <div
                        className="mb-3 -mx-4 -mt-3 pt-3 px-4 pb-2 bg-slate-900/60 rounded-t-2xl border-l-4 border-purple-500 cursor-pointer hover:bg-slate-900/80 transition-colors"
                        onClick={() => {
                          const el = document.querySelector(`[data-message-id="${msg.replyTo?.messageId}"]`);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "center" });
                            el.classList.add("ring-2", "ring-purple-400", "ring-opacity-70");
                            setTimeout(() => el.classList.remove("ring-2", "ring-purple-400", "ring-opacity-70"), 2000);
                          }
                        }}
                      >
                        <div className="text-sm font-semibold text-purple-300 mb-1">
                          {msg.replyTo.senderName}
                        </div>
                        <div className="text-base text-purple-100 line-clamp-2 break-words">
                          {msg.replyTo.text}
                        </div>
                      </div>
                    )}

                    {isEditing ? (
                      <div className="space-y-3 -mx-1 -my-1">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          autoFocus
                          rows={Math.min(8, editText.split("\n").length + 2)}
                          className={documentChatTheme.editingTextarea}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              saveEdit(msg.id);
                            }
                            if (e.key === "Escape") {
                              cancelEdit();
                            }
                          }}
                          placeholder="Редактируйте сообщение..."
                        />
                        <div className="flex justify-end gap-3 text-base pt-1">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="px-4 py-1.5 text-purple-300 hover:text-purple-100 hover:bg-purple-900/40 rounded-md transition-colors"
                          >
                            Отмена
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(msg.id)}
                            disabled={!editText.trim() || editText.trim() === msg.text?.trim()}
                            className={cn(
                              "px-5 py-1.5 rounded-md font-medium transition-colors",
                              editText.trim() && editText.trim() !== msg.text?.trim()
                                ? "bg-purple-600 hover:bg-purple-700 text-white"
                                : "bg-purple-800/50 text-purple-300 cursor-not-allowed"
                            )}
                          >
                            Сохранить
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {isFirstInGroup && !isOwn && (
                          <div className="text-base font-medium text-purple-200/90 mb-1.5">
                            {msg.senderName}
                          </div>
                        )}
                        <p className="break-words whitespace-pre-wrap">{msg.text}</p>
                        {msg.edited && (
                          <span className="text-sm text-purple-300/70 italic ml-1">(изменено)</span>
                        )}
                        {msg.file && (
                          <div className={documentChatTheme.fileAttachment}>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <FileText className="h-5 w-5 text-purple-300 flex-shrink-0" />
                              <span className="text-base font-medium text-white break-all line-clamp-2" title={msg.file.name}>
                                {msg.file.name}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-purple-300 hover:text-white hover:bg-purple-700/50 flex-shrink-0 mt-2 sm:mt-0"
                              onClick={() => forceDownload(msg.file!.url, msg.file!.name)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-end gap-2 text-sm opacity-85">
                          <span>{getTimestamp(msg.timestamp)}</span>
                          {isOwn && (
                            <span className="ml-1 flex items-center gap-1">
                              {allRead ? (
                                <CheckCheck className="h-4 w-4 text-cyan-300" />
                              ) : (
                                <Check className="h-4 w-4 text-slate-400" />
                              )}
                              <span className={cn("text-sm font-medium", allRead ? "text-cyan-300" : "text-slate-400")}>
                                {readCount}/{totalParticipants}
                              </span>
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </MessageBubble>

                  {isOwn && (
                    <Avatar className="mb-1 ml-3 h-8 w-8 flex-shrink-0">
                      <AvatarFallback className={documentChatTheme.avatarOwn}>
                        {getInitials(msg.senderName)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {isOwn || <div className="w-11 flex-shrink-0" />}
                </motion.div>
              </div>
            );
          })
        )}

        {typingUser && (
          <div className={documentChatTheme.typingIndicator}>
            {typingUser} печатает...
          </div>
        )}
      </div>

      {replyTo && (
        <div className={documentChatTheme.replyBanner}>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-purple-300 font-medium">Ответ на:</div>
            <div className="text-base text-white truncate">
              {replyTo.senderName}: {replyTo.text.slice(0, 80)}
              {replyTo.text.length > 80 ? "..." : ""}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-300 hover:text-white" onClick={cancelReply}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}

      <div className={documentChatTheme.inputBar}>
        <div className="flex items-center gap-4">
          <label className="cursor-pointer p-3 hover:bg-purple-900/40 rounded-full transition-colors">
            <input
              type="file"
              className="hidden"
              ref={fileInputRef}
              onChange={(e) => setAttachedFile(e.target.files?.[0] ?? null)}
            />
            <Paperclip className="h-7 w-7 text-purple-300 hover:text-purple-100 transition" />
          </label>

          {attachedFile && (
            <Badge
              variant="secondary"
              className="bg-purple-900/60 text-purple-100 border-purple-600/60 flex items-center gap-2 px-5 py-2 text-base max-w-[240px]"
            >
              <span className="truncate">{attachedFile.name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full hover:bg-purple-800/60 p-0"
                onClick={() => setAttachedFile(null)}
              >
                <X className="h-5 w-5" />
              </Button>
            </Badge>
          )}

          <Input
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Напиши сообщение..."
            className="flex-1 bg-slate-900/80 border-purple-700/60 text-white placeholder:text-purple-400/70 rounded-full px-6 py-3 text-base shadow-inner focus-visible:ring-purple-500 h-12"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />

          <Button
            onClick={sendMessage}
            disabled={isSending || (!messageText.trim() && !attachedFile)}
            className={cn(
              "rounded-full h-12 w-12 p-0 transition-all flex items-center justify-center shadow-lg",
              isSending
                ? "bg-purple-800/60"
                : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-purple-900/40"
            )}
          >
            {isSending ? (
              <div className="h-7 w-7 animate-spin rounded-full border-3 border-white border-t-transparent" />
            ) : (
              <Send className="h-7 w-7" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}